import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Download, Eye, EyeOff, Type, FileText, Code, Image } from 'lucide-react';
import Modal from './Modal';
import '../styles/FileEditor.css';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const caseStoragePath = (file) => String(
    file?.key || file?.file_path || file?.path || ''
).replace(/^\/+/, '').trim();

const caseStorageParts = (path) => {
    const match = String(path || '').match(/^cases\/([1-9]\d*)\/(.+)$/);
    if (!match || !match[2] || !match[2].includes('/')) {
        throw new Error('The selected file does not have a valid case document path.');
    }

    const relativeParts = match[2].split('/').filter(Boolean);
    if (relativeParts.length < 2) {
        throw new Error('The selected file does not have a valid case document path.');
    }

    return {
        relativePath: relativeParts.join('/'),
        folderPath: relativeParts.slice(0, -1).join('/'),
        fileName: relativeParts[relativeParts.length - 1],
    };
};

const responseJson = async (response, fallbackMessage) => {
    let result;
    try {
        result = await response.json();
    } catch (_) {
        throw new Error(fallbackMessage);
    }
    if (!response.ok || !result || typeof result !== 'object') {
        throw new Error(result?.message || result?.error || fallbackMessage);
    }
    return result;
};

const FileEditor = ({ file, isOpen, onClose, onSave, case_id }) => {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editorType, setEditorType] = useState('text');
    const [originalContent, setOriginalContent] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [preview, setPreview] = useState(false);
    const editorRef = useRef(null);

    // Determine editor type based on file extension
    const getEditorType = (fileName) => {
        const ext = fileName.toLowerCase().split('.').pop();
        
        const editorTypes = {
            // Text files
            'txt': 'text',
            'md': 'markdown',
            'markdown': 'markdown',
            
            // Code files
            'js': 'javascript',
            'jsx': 'javascript',
            'ts': 'typescript',
            'tsx': 'typescript',
            'html': 'html',
            'htm': 'html',
            'css': 'css',
            'scss': 'scss',
            'sass': 'scss',
            'json': 'json',
            'xml': 'xml',
            'sql': 'sql',
            'py': 'python',
            'php': 'php',
            'java': 'java',
            'c': 'c',
            'cpp': 'cpp',
            'h': 'c',
            'cs': 'csharp',
            'go': 'go',
            'rb': 'ruby',
            'sh': 'bash',
            'bat': 'batch',
            'ps1': 'powershell',
            'yaml': 'yaml',
            'yml': 'yaml',
            
            // Rich text
            'rtf': 'richtext',
            
            // Structured data
            'csv': 'csv',
            
            // Web
            'svg': 'svg'
        };
        
        return editorTypes[ext] || 'text';
    };

    // Check if file type is editable
    const isEditableFile = (fileName) => {
        const ext = fileName.toLowerCase().split('.').pop();
        const editableExtensions = [
            // Text files
            'txt', 'md', 'markdown', 'rtf',
            
            // Code files
            'js', 'jsx', 'ts', 'tsx', 'html', 'htm', 'css', 'scss', 'sass',
            'json', 'xml', 'sql', 'py', 'php', 'java', 'c', 'cpp', 'h',
            'cs', 'go', 'rb', 'sh', 'bat', 'ps1', 'yaml', 'yml',
            
            // Data files
            'csv', 'tsv',
            
            // Web files
            'svg',
            
            // Config files
            'ini', 'conf', 'config', 'env', 'gitignore', 'gitattributes',
            
            // Documentation
            'readme', 'license', 'changelog'
        ];
        
        return editableExtensions.includes(ext);
    };

    // Load file content
    const loadFileContent = async () => {
        if (!file || !isEditableFile(file.name)) return;
        
        setLoading(true);
        try {
            const editorCaseId = String(case_id ?? '').trim();
            if (!/^[1-9]\d*$/.test(editorCaseId)) {
                throw new Error('A valid case is required to edit this file.');
            }
            const fileIdentifier = caseStoragePath(file);
            caseStorageParts(fileIdentifier);

            // Get signed URL for the file
            const signedUrl = new URL('https://api.casedb.co/get-signed-url.php');
            signedUrl.searchParams.set('key', fileIdentifier);
            signedUrl.searchParams.set('cases', 'true');
            signedUrl.searchParams.set('case_id', editorCaseId);
            const response = await fetch(signedUrl.toString(), { credentials: 'include' });
            const result = await responseJson(response, 'Failed to get file URL');
            if (result.success !== true || typeof result.url !== 'string' || !result.url.trim()) {
                throw new Error(result.message || 'Failed to get file URL');
            }
            
            // Fetch file content
            const fileResponse = await fetch(result.url);
            if (!fileResponse.ok) {
                throw new Error(`The file content could not be loaded (HTTP ${fileResponse.status || 'error'}).`);
            }
            const text = await fileResponse.text();
            
            setContent(text);
            setOriginalContent(text);
            setEditorType(getEditorType(file.name));
            setHasChanges(false);
        } catch (error) {
            console.error('Error loading file:', error);
            alert(`Failed to load file: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && file) {
            loadFileContent();
        }
    }, [isOpen, file]);

    // Handle content changes
    const handleContentChange = (newContent) => {
        setContent(newContent);
        setHasChanges(newContent !== originalContent);
    };

    // Save file
    const handleSave = async () => {
        if (!hasChanges) return;
        
        setSaving(true);
        try {
            const fileIdentifier = caseStoragePath(file);
            const source = caseStorageParts(fileIdentifier);

            // Convert content to base64
            const base64Content = btoa(unescape(encodeURIComponent(content)));
            
            // Upload updated file
            const uploadData = new FormData();
            uploadData.append("case_id", case_id);
            uploadData.append("lead_id", file.lead_id || '');
            // upload.php expects a path relative to cases/{storage id}/. Sending
            // the complete S3 key here nests a second cases/{id}/ prefix and can
            // make a successful response point at a different object.
            uploadData.append("folder_path", source.folderPath);
            uploadData.append("file_name", source.fileName);
            uploadData.append("file_content", base64Content);

            await ensureCaseDbCsrfToken();
            const response = await fetch("https://api.casedb.co/upload.php", {
                method: "POST",
                headers: withCsrfHeaders({}, 'POST'),
                body: uploadData,
                credentials: "include"
            });

            const result = await responseJson(response, 'The save response could not be read');
            const savedFiles = Array.isArray(result.uploaded_files) ? result.uploaded_files : [];
            const savedAtRequestedPath = savedFiles.some(savedFile => {
                const returnedPath = String(savedFile?.file_path || savedFile?.path || '').replace(/^\/+/, '');
                return returnedPath === fileIdentifier;
            });

            if (result.success !== true) {
                throw new Error(result.message || 'Upload failed');
            }
            if (!savedAtRequestedPath) {
                throw new Error('CaseDB did not confirm that the original file was updated.');
            }

            setOriginalContent(content);
            setHasChanges(false);
            if (onSave) onSave(file, content);
            alert('File saved successfully!');
        } catch (error) {
            console.error('Error saving file:', error);
            alert(`Failed to save file: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    // Handle close with unsaved changes warning
    const handleClose = () => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    // Render syntax highlighting based on editor type
    const renderEditor = () => {
        const editorProps = {
            value: content,
            onChange: (e) => handleContentChange(e.target.value),
            className: `file-editor-textarea ${editorType}`,
            placeholder: `Edit ${file?.name || 'file'}...`,
            spellCheck: editorType === 'text' || editorType === 'markdown'
        };

        return (
            <div className="editor-container">
                <div className="editor-toolbar">
                    <div className="editor-info">
                        <span className="file-type-badge">{editorType.toUpperCase()}</span>
                        <span className="file-name">{file?.name}</span>
                        {hasChanges && <span className="unsaved-indicator">●</span>}
                    </div>
                    <div className="editor-actions">
                        <button
                            className="editor-btn"
                            onClick={() => setPreview(!preview)}
                            title={preview ? "Show Editor" : "Show Preview"}
                        >
                            {preview ? <EyeOff size={16} /> : <Eye size={16} />}
                            {preview ? "Edit" : "Preview"}
                        </button>
                    </div>
                </div>
                
                {preview && (editorType === 'markdown' || editorType === 'html') ? (
                    <div className="editor-preview">
                        {editorType === 'markdown' ? (
                            <div 
                                className="markdown-preview"
                                dangerouslySetInnerHTML={{ 
                                    __html: markdownToHtml(content) 
                                }}
                            />
                        ) : (
                            <iframe
                                srcDoc={content}
                                className="html-preview"
                                title="HTML Preview"
                            />
                        )}
                    </div>
                ) : (
                    <textarea
                        {...editorProps}
                        ref={editorRef}
                        rows={20}
                    />
                )}
            </div>
        );
    };

    // Simple markdown to HTML converter
    const markdownToHtml = (markdown) => {
        return markdown
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/^\> (.*$)/gim, '<blockquote>$1</blockquote>')
            .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
            .replace(/\*(.*)\*/gim, '<em>$1</em>')
            .replace(/\`(.*)\`/gim, '<code>$1</code>')
            .replace(/\n/gim, '<br>');
    };

    if (!isOpen || !file) return null;

    if (!isEditableFile(file.name)) {
        return (
            <Modal
                title="File Not Editable"
                instructions={`The file "${file.name}" cannot be edited. Supported formats include text files, code files, markdown, HTML, CSS, JSON, XML, and more.`}
                onClose={onClose}
            >
                <div className="modal-content-wrapper non-editable-file">
                    <div className="file-icon">
                        <FileText size={48} />
                    </div>
                    <h3>{file.name}</h3>
                    <p>This file type is not supported for editing in the browser.</p>
                    <div className="file-actions">
                        <button className="action alt" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            title={`Edit File`}
            onClose={handleClose}
            className="file-editor-modal"
            size="large"
        >
            <div className="modal-content-wrapper file-editor">
                {loading ? (
                    <div className="editor-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading file content...</p>
                    </div>
                ) : (
                    <>
                        {renderEditor()}
                        <div className="editor-footer">
                            <div className="editor-stats">
                                <span>{content.length} characters</span>
                                <span>{content.split('\n').length} lines</span>
                            </div>
                            <div className="editor-footer-actions">
                                <button 
                                    className="action alt" 
                                    onClick={handleClose}
                                >
                                    <X size={16} /> Cancel
                                </button>
                                <button 
                                    className="action" 
                                    onClick={handleSave}
                                    disabled={!hasChanges || saving}
                                >
                                    <Save size={16} />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default FileEditor;
